import React, { useMemo } from "react"

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  type PaginationState,
  useReactTable,
} from "@tanstack/react-table"
import { produce } from "immer"
import { Plus } from "lucide-react"
import { DateTime } from "luxon"
import { useWatch, type UseFormReturn } from "react-hook-form"

import Paginator from "@/components/Paginator.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import type { AggregationMode } from "@/types/dataPointTypes.ts"

import DataPointHeader from "./DataPointHeader.tsx"
import DataPointSelector from "./DataPointSelector.tsx"

import type { DataPointReportBuilderSchema } from "./DataPointReportBuilder.tsx"
import type { DataPointReportRow } from "../../types/reportTypes.ts"

const columnHelper = createColumnHelper<DataPointReportRow>()

export default function DataPointReportTable({
  form,
  data,
  isLoading,
  pagination,
  setPagination,
  rowCount,
}: {
  form: UseFormReturn<DataPointReportBuilderSchema>
  data: DataPointReportRow[]
  isLoading: boolean
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  rowCount: number
}) {
  const watchedDataPoints = useWatch({
    control: form.control,
    name: "dataPoints",
  })
  const watchedAggModes = useWatch({
    control: form.control,
    name: "aggModes",
  })

  const dataPoints = useMemo(() => watchedDataPoints ?? [], [watchedDataPoints])

  const aggModes: AggregationMode[] = useMemo(
    () => (watchedAggModes ?? []) as AggregationMode[],
    [watchedAggModes],
  )

  const columnDefs = useMemo(() => {
    const timestampCol = columnHelper.accessor((dpv) => dpv.timestamp, {
      id: "timestamp",
      header: () => (
        <div className="py-2 px-4 w-[225px] h-[175px] flex justify-end flex-col font-semibold">
          Timestamp
        </div>
      ),
      cell: (context) => (
        <TableCell className="px-4">
          {DateTime.fromISO(context.getValue()).toFormat("D, ttt")}
        </TableCell>
      ),
    })

    const dpCols =
      dataPoints?.map((dpId: number, index) =>
        columnHelper.accessor((dp) => dp.values[index], {
          id: dpId.toString(),
          header: () => (
            <div className="w-[250px] py-2 px-0">
              <DataPointHeader
                id={dpId}
                onRemove={() => {
                  form.setValue(
                    "dataPoints",
                    produce(dataPoints, (draft) => {
                      draft.splice(index, 1)
                    }),
                  )
                }}
                setAggMode={(a) => {
                  form.setValue(
                    "aggModes",
                    produce(aggModes, (draft: AggregationMode[]) => {
                      draft[index] = a
                    }),
                  )
                }}
                last={index == dataPoints.length - 1}
                aggMode={aggModes[index]}
                index={index}
                setIndex={(i) =>
                  form.setValue(
                    "dataPoints",
                    produce(dataPoints, (draft) => {
                      draft.splice(index, 1)
                      draft.splice(i, 0, dpId)
                    }),
                  )
                }
              />
            </div>
          ),
          cell: (context) => (
            <TableCell className="text-right pr-8">
              {context.getValue()?.toLocaleString() || (
                <div className="bg-muted text-muted-foreground p-2 ml-4 rounded-md">N/A</div>
              )}
            </TableCell>
          ),
        }),
      ) || []
    const addCol = columnHelper.display({
      id: "add",
      header: () => (
        <div className="w-full h-full py-2">
          <DataPointSelector
            onSelect={(id) => {
              form.setValue("dataPoints", [...dataPoints, id])
              form.setValue("aggModes", [...aggModes, "Average"])
            }}
          >
            <Button
              type="button"
              variant="outline"
              className="h-full flex flex-col justify-center w-[175px] border-dashed font-semibold"
            >
              <Plus />
              Add Data Point
            </Button>
          </DataPointSelector>
        </div>
      ),
      cell: () => <TableCell></TableCell>,
    })
    return [timestampCol, ...dpCols, addCol]
  }, [dataPoints, aggModes, form])

  const table = useReactTable<DataPointReportRow>({
    data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      pagination,
    },
  })
  return (
    <div className="rounded-md shadow-sm border overflow-x-hidden">
      <div className="w-full overflow-x-auto">
        <Table className="">
          {table.getHeaderGroups().map((hg, i) => (
            <TableHeader
              className="bg-white sticky top-0 border-b shadow rounded-t-md z-10"
              key={i}
            >
              <TableRow key="header-row">
                {hg.headers.map((header, i) => (
                  <TableHead key={i} className={i == hg.headers.length - 1 ? "w-full" : ""}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
          ))}
          {isLoading ? (
            <TableBody>
              {[...Array(pagination.pageSize).keys()].map((i) => (
                <TableRow key={i}>
                  <TableCell colSpan={2 + dataPoints.length} className="h-[37px]">
                    <Skeleton className="w-full h-full z-1" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <React.Fragment key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </React.Fragment>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </div>
      <div>
        <Paginator table={table} />
      </div>
    </div>
  )
}
